import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MessageSquare, Sparkles, Zap, Users, ArrowRight } from "lucide-react";
import api from "@/lib/api";

/**
 * DiscordLinkBanner — persistent Dashboard banner for PAID members who
 * haven't linked Discord. Non-dismissible on purpose: the community is
 * half the value of a paid tier, so skipping it silently is a bigger
 * disservice than banner fatigue.
 *
 * Auto-hides once `user.discord_linked` flips to true (parent reloads
 * `/auth/me` on OAuth-callback return via existing PaymentReturn flow).
 * Free tier never sees this — they don't have Discord access anyway.
 */
export default function DiscordLinkBanner({ user }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const shouldShow = useMemo(() => {
    if (!user) return false;
    if (user.tier === "free") return false;
    if (user.discord_linked) return false;
    if (user.role === "admin") return false; // admin bypasses
    return true;
  }, [user]);

  if (!shouldShow) return null;

  const link = async () => {
    try {
      const { data } = await api.get("/discord/oauth/url");
      if (data?.url) window.location.href = data.url;
      else navigate("/account?tab=discord");
    } catch {
      navigate("/account?tab=discord");
    }
  };

  return (
    <section
      data-testid="dashboard-discord-link-banner"
      aria-live="polite"
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#5865F2] via-[#4752c4] to-[#3c45a5] text-white shadow-[0_20px_50px_-20px_rgba(88,101,242,0.5)]"
    >
      {/* Soft depth glows */}
      <div aria-hidden="true" className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-white/10 blur-3xl pointer-events-none" />
      <div aria-hidden="true" className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-amber-400/15 blur-3xl pointer-events-none" />

      <div className="relative p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-center">
        <div>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 border border-white/25 px-3 py-1 text-[10px] uppercase tracking-[0.28em] font-bold">
            <MessageSquare size={11} /> {t("discordBannerUi.eyebrow")}
          </div>
          <h2 className="mt-4 font-display font-black text-2xl sm:text-3xl leading-[1.1] tracking-tighter">
            {t("discordBannerUi.headline")}
          </h2>
          <p className="mt-3 text-sm sm:text-base text-white/85 max-w-2xl leading-relaxed">
            {t("discordBannerUi.body")}{" "}
            <a href="/discord-guide" data-testid="dashboard-discord-guide-link" className="font-semibold underline underline-offset-2 text-white hover:text-amber-200">
              {t("discordGuide.newToDiscord")}
            </a>
          </p>

          {/* Value-prop chips */}
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/20 px-3 py-1.5 text-xs font-semibold">
              <Zap size={11} className="text-amber-300" /> {t("discordBannerUi.chipSetups")}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/20 px-3 py-1.5 text-xs font-semibold">
              <Sparkles size={11} className="text-amber-300" /> {t("discordBannerUi.chipRecap")}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/20 px-3 py-1.5 text-xs font-semibold">
              <Users size={11} className="text-amber-300" /> {t("discordBannerUi.chipCommunity")}
            </span>
          </div>
        </div>

        <div className="lg:pl-6 lg:border-l lg:border-white/15 flex flex-col items-stretch lg:items-end gap-2">
          <button
            type="button"
            onClick={link}
            data-testid="dashboard-discord-link-cta"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-[#4752c4] font-semibold px-5 py-3 text-sm hover:bg-slate-50 shadow-md transition-colors whitespace-nowrap"
          >
            {t("discordBannerUi.cta")}
            <ArrowRight size={14} />
          </button>
          <p className="text-[11px] text-white/60 text-center lg:text-right">
            {t("discordBannerUi.trust")}
          </p>
        </div>
      </div>
    </section>
  );
}
